import { useEffect, useState } from "react";
import { useWorkspace } from "../app/workspace";
import { Icon } from "../components/icons";
import { Empty } from "../components/ui";
import { shortDate } from "../lib/format";
import type { SuccessPlan, Objective, ObjectiveStatus, PlanStatus, Assignee, Workspace } from "../lib/types";
import * as work from "../lib/workClient";

const PLAN_LABEL: Record<PlanStatus, string> = { draft: "Draft", on_track: "On track", at_risk: "At risk", achieved: "Achieved" };
const PLAN_CLASS: Record<PlanStatus, string> = { draft: "badge--neutral", on_track: "badge--good", at_risk: "badge--risk", achieved: "badge--good" };
const OBJ_NEXT: Record<ObjectiveStatus, ObjectiveStatus> = { todo: "in_progress", in_progress: "done", done: "blocked", blocked: "todo" };
const OBJ_COLOR: Record<ObjectiveStatus, string> = { todo: "var(--faint)", in_progress: "var(--watch)", done: "var(--good)", blocked: "var(--risk)" };
const OBJ_LABEL: Record<ObjectiveStatus, string> = { todo: "To do", in_progress: "In progress", done: "Done", blocked: "Blocked" };
const encA = (a?: Assignee) => (!a ? "none" : a.kind === "role" ? `role:${a.role}` : `user:${a.userId}`);
const parseA = (v: string): Assignee | undefined => { if (v === "none") return undefined; const [k, id] = v.split(":"); return k === "role" ? { kind: "role", role: id } : { kind: "user", userId: id }; };
const uid = (p: string) => `${p}_${Date.now().toString(36)}${Math.floor(Math.random() * 1e3).toString(36)}`;
const progress = (p: SuccessPlan) => (p.objectives.length ? Math.round((p.objectives.filter((o) => o.status === "done").length / p.objectives.length) * 100) : 0);

export default function SuccessPlans() {
  const { ws } = useWorkspace();
  const [mode, setMode] = useState<work.Mode>("local");
  const [plans, setPlans] = useState<SuccessPlan[]>(ws.successPlans);
  const [selId, setSelId] = useState<string>(ws.successPlans[0]?.id ?? "");
  const [composing, setComposing] = useState(false);

  useEffect(() => { work.loadPlans(ws).then((r) => { setMode(r.mode); setPlans(r.plans); if (!r.plans.some((p) => p.id === selId)) setSelId(r.plans[0]?.id ?? ""); }); }, [ws]);

  const plan = plans.find((p) => p.id === selId) ?? null;
  const onTrack = plans.filter((p) => p.status === "on_track" || p.status === "achieved").length;
  const atRisk = plans.filter((p) => p.status === "at_risk").length;
  const objDone = plans.reduce((n, p) => n + p.objectives.filter((o) => o.status === "done").length, 0);
  const objTotal = plans.reduce((n, p) => n + p.objectives.length, 0);

  const save = async (next: SuccessPlan) => {
    setPlans((prev) => prev.map((p) => (p.id === next.id ? next : p)));
    await work.updatePlan(mode, ws, next.id, { title: next.title, outcome: next.outcome, targetDate: next.targetDate, status: next.status, objectives: next.objectives });
  };
  const patchPlan = (p: Partial<SuccessPlan>) => { if (plan) save({ ...plan, ...p }); };
  const setObjectives = (objectives: Objective[]) => { if (plan) { const status = objectives.length === 0 ? "draft" : objectives.every((o) => o.status === "done") ? "achieved" : objectives.some((o) => o.status === "blocked") ? "at_risk" : "on_track"; save({ ...plan, objectives, status }); } };

  const create = async (accountId: string, title: string, outcome: string) => {
    const p = await work.createPlan(mode, ws, { accountId, title, outcome });
    setPlans((prev) => [...prev, p]); setSelId(p.id); setComposing(false);
  };
  const remove = async (p: SuccessPlan) => { setPlans((prev) => prev.filter((x) => x.id !== p.id)); if (selId === p.id) setSelId(""); await work.deletePlan(mode, ws, p.id); };

  return (
    <div className="page">
      <div className="row between mb-24" style={{ alignItems: "flex-start" }}>
        <div>
          <p className="eyebrow">Success Plans</p>
          <h1 style={{ fontFamily: "var(--serif)", fontSize: 28, marginTop: 6 }}>What success looks like</h1>
          <p className="page__intro" style={{ marginBottom: 0 }}>
            The outcome each customer is buying, and the measurable objectives to get there — owned, dated, and tracked.
          </p>
        </div>
        <div className="row" style={{ gap: 10 }}>
          <ModePill mode={mode} />
          <button className="btn btn--primary btn--sm" onClick={() => setComposing((v) => !v)}><Icon name="plus" size={15} /> New plan</button>
        </div>
      </div>

      <div className="stats">
        <Stat icon="target" label="Active plans" value={`${plans.length}`} foot="Across the book" />
        <Stat icon="check" label="On track" value={`${onTrack}`} foot="On track or achieved" color="var(--good)" />
        <Stat icon="pulse" label="At risk" value={`${atRisk}`} foot="Need attention" color={atRisk ? "var(--risk)" : undefined} />
        <Stat icon="route" label="Objectives done" value={`${objDone}/${objTotal}`} foot="Across all plans" />
      </div>

      {composing && <Composer ws={ws} onCancel={() => setComposing(false)} onCreate={create} />}

      <div className="auto-layout">
        <div style={{ display: "grid", gap: 12 }}>
          {plans.length === 0 ? <Empty>No success plans yet.</Empty> : plans.map((p) => {
            const active = p.id === selId;
            return (
              <div key={p.id} className="card" onClick={() => setSelId(p.id)}
                style={{ cursor: "pointer", borderColor: active ? "var(--corona)" : undefined, boxShadow: active ? "0 0 0 1px var(--corona), var(--shadow)" : undefined }}>
                <div className="card__bd">
                  <div className="row between" style={{ alignItems: "flex-start" }}>
                    <div>
                      <b style={{ fontSize: 14.5 }}>{p.title}</b>
                      <small className="faint" style={{ display: "block", fontSize: 12.5 }}>{p.accountName}</small>
                    </div>
                    <span className={`badge ${PLAN_CLASS[p.status]}`}>{PLAN_LABEL[p.status]}</span>
                  </div>
                  <div className="row between" style={{ marginTop: 12, marginBottom: 6 }}>
                    <span className="faint" style={{ fontSize: 12 }}>{p.objectives.filter((o) => o.status === "done").length}/{p.objectives.length} objectives</span>
                    {p.targetDate && <span className="faint" style={{ fontSize: 12 }}>target {shortDate(p.targetDate)}</span>}
                  </div>
                  <div className="ent-bar"><span style={{ width: `${progress(p)}%`, background: p.status === "at_risk" ? "var(--risk)" : "var(--good)" }} /></div>
                </div>
              </div>
            );
          })}
        </div>

        <div>
          {plan ? (
            <PlanEditor key={plan.id} ws={ws} plan={plan} onPatch={patchPlan} onObjectives={setObjectives} onDelete={() => remove(plan)} />
          ) : (
            <div className="card"><div className="card__bd"><Empty>Select a plan, or create one.</Empty></div></div>
          )}
        </div>
      </div>
    </div>
  );
}

function PlanEditor({ ws, plan, onPatch, onObjectives, onDelete }: {
  ws: Workspace; plan: SuccessPlan; onPatch: (p: Partial<SuccessPlan>) => void; onObjectives: (o: Objective[]) => void; onDelete: () => void;
}) {
  const setObj = (i: number, patch: Partial<Objective>) => onObjectives(plan.objectives.map((o, j) => (j === i ? { ...o, ...patch } : o)));
  return (
    <div className="card">
      <div className="card__hd">
        <input className="inp inp--title" value={plan.title} onChange={(e) => onPatch({ title: e.target.value })} />
        <button className="icon-btn" title="Delete plan" onClick={onDelete}><Icon name="dot" size={16} /></button>
      </div>
      <div className="card__bd" style={{ display: "grid", gap: 16 }}>
        <label className="labeled"><span className="labeled__l">Outcome — the value they're buying</span>
          <textarea className="inp" rows={2} value={plan.outcome} onChange={(e) => onPatch({ outcome: e.target.value })} /></label>
        <div className="composer__grid" style={{ gridTemplateColumns: "160px 1fr" }}>
          <label className="labeled"><span className="labeled__l">Target date</span>
            <input className="inp" type="date" value={plan.targetDate ?? ""} onChange={(e) => onPatch({ targetDate: e.target.value })} /></label>
          <label className="labeled"><span className="labeled__l">Account</span>
            <input className="inp" value={plan.accountName} disabled /></label>
        </div>

        <section className="rule-sec">
          <div className="row between">
            <div className="rule-sec__hd" style={{ marginBottom: 0 }}><span className="rule-sec__k">Objectives</span> {plan.objectives.filter((o) => o.status === "done").length} of {plan.objectives.length} done</div>
            <button className="btn btn--sm btn--ghost" onClick={() => onObjectives([...plan.objectives, { id: uid("o"), title: "New objective", status: "todo" }])}><Icon name="plus" size={13} /> Add objective</button>
          </div>
          <div className="obj-list">
            {plan.objectives.length === 0 ? <div className="preview-empty">No objectives yet — add the measurable steps.</div> : plan.objectives.map((o, i) => (
              <div key={o.id} className="obj">
                <button className="obj__status" title={`${OBJ_LABEL[o.status]} — click to advance`} onClick={() => setObj(i, { status: OBJ_NEXT[o.status] })}>
                  <span className="obj__dot" style={{ background: OBJ_COLOR[o.status], borderColor: OBJ_COLOR[o.status] }}>{o.status === "done" ? <Icon name="check" size={11} /> : null}</span>
                </button>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <input className="obj__title" value={o.title} onChange={(e) => setObj(i, { title: e.target.value })} style={{ textDecoration: o.status === "done" ? "line-through" : "none" }} />
                  <div className="row" style={{ gap: 8, marginTop: 4, flexWrap: "wrap" }}>
                    <input className="obj__meta" placeholder="Success metric" value={o.metric ?? ""} onChange={(e) => setObj(i, { metric: e.target.value })} />
                    <select className="obj__meta" value={encA(o.owner)} onChange={(e) => setObj(i, { owner: parseA(e.target.value) })}>
                      <option value="none">Unassigned</option>
                      <optgroup label="Roles">{ws.roles.map((r) => <option key={r.key} value={`role:${r.key}`}>{r.label}</option>)}</optgroup>
                      <optgroup label="People">{ws.users.map((u) => <option key={u.id} value={`user:${u.id}`}>{u.name}</option>)}</optgroup>
                    </select>
                    <input className="obj__meta" type="date" value={o.dueDate ?? ""} onChange={(e) => setObj(i, { dueDate: e.target.value })} />
                  </div>
                </div>
                <span className="tag" style={{ color: OBJ_COLOR[o.status], alignSelf: "flex-start" }}>{OBJ_LABEL[o.status]}</span>
                <button className="mini-btn" style={{ alignSelf: "flex-start" }} onClick={() => onObjectives(plan.objectives.filter((_, j) => j !== i))}><Icon name="dot" size={13} /></button>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function Composer({ ws, onCancel, onCreate }: { ws: Workspace; onCancel: () => void; onCreate: (accountId: string, title: string, outcome: string) => void }) {
  const [accountId, setAccountId] = useState(ws.accounts[0]?.id ?? "");
  const [title, setTitle] = useState("");
  const [outcome, setOutcome] = useState("");
  return (
    <div className="card composer">
      <div className="card__bd">
        <input className="inp inp--title" placeholder="Plan title — e.g. FY26 expansion" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
        <div className="composer__grid" style={{ gridTemplateColumns: "1fr 2fr", marginTop: 12 }}>
          <label className="labeled"><span className="labeled__l">Account</span>
            <select className="inp" value={accountId} onChange={(e) => setAccountId(e.target.value)}>{ws.accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></label>
          <label className="labeled"><span className="labeled__l">Outcome</span>
            <input className="inp" placeholder="The value they're buying" value={outcome} onChange={(e) => setOutcome(e.target.value)} /></label>
        </div>
        <div className="row" style={{ gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
          <button className="btn btn--sm" onClick={onCancel}>Cancel</button>
          <button className="btn btn--primary btn--sm" disabled={!title.trim()} onClick={() => onCreate(accountId, title.trim(), outcome.trim())}>Create plan</button>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, foot, color }: { icon: string; label: string; value: string; foot: string; color?: string }) {
  return (
    <div className="stat">
      <div className="stat__label"><Icon name={icon} size={14} /> {label}</div>
      <div className="stat__value" style={{ color }}>{value}</div>
      <div className="stat__foot">{foot}</div>
    </div>
  );
}
function ModePill({ mode }: { mode: work.Mode }) {
  return <span className={`mode-pill ${mode === "api" ? "live" : ""}`} title={mode === "api" ? "Connected to the Penumbrai API" : "Standalone demo — changes saved to this browser"}><i />{mode === "api" ? "Live API" : "Local demo"}</span>;
}
